import { useState, useRef, useEffect, useCallback, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

interface Props {
  content: ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
  /** 点击触发（默认 hover 触发） */
  trigger?: 'hover' | 'click';
  className?: string;
}

export default function Tooltip({ content, children, position = 'top', width = 200, trigger = 'hover', className }: Props) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  const containerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => setVisible(true), []);
  const hide = useCallback(() => setVisible(false), []);
  const toggle = useCallback(() => setVisible(v => !v), []);

  // 计算 tooltip 位置（基于目标元素在视口中的坐标）
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tw = tooltipEl?.offsetWidth || width;
    const th = tooltipEl?.offsetHeight || 80;
    const gap = 10;

    let left = 0, top = 0;

    switch (position) {
      case 'top':
        left = rect.left + rect.width / 2 - tw / 2;
        top = rect.top - th - gap;
        break;
      case 'bottom':
        left = rect.left + rect.width / 2 - tw / 2;
        top = rect.bottom + gap;
        break;
      case 'left':
        left = rect.left - tw - gap;
        top = rect.top + rect.height / 2 - th / 2;
        break;
      case 'right':
        left = rect.right + gap;
        top = rect.top + rect.height / 2 - th / 2;
        break;
    }

    // 边界检测：防止超出视口
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left < 8) left = 8;
    if (left + tw > vw - 8) left = vw - tw - 8;
    if (top < 8) {
      // 如果上方空间不够，切换到下方
      if (position === 'top') {
        top = rect.bottom + gap;
      } else {
        top = 8;
      }
    }
    if (top + th > vh - 8) {
      if (position === 'bottom') {
        top = rect.top - th - gap;
      } else {
        top = vh - th - 8;
      }
    }

    setCoords({ left, top });
  }, [position, width]);

  // visible 变化时更新位置
  useLayoutEffect(() => {
    if (visible) {
      updatePosition();
      // tooltip 渲染后再精确计算一次
      requestAnimationFrame(updatePosition);
    }
  }, [visible, updatePosition]);

  // 窗口滚动/resize 时更新位置
  useEffect(() => {
    if (!visible) return;
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [visible, updatePosition]);

  // 点击外部关闭
  useEffect(() => {
    if (!visible || trigger !== 'click') return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [visible, trigger]);

  const eventHandlers = trigger === 'click'
    ? { onClick: toggle }
    : { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide };

  const tooltipEl = visible && (
    <div
      ref={tooltipRef}
      className={`${styles.tooltipPortal} ${styles[`portal${position.charAt(0).toUpperCase() + position.slice(1)}`]}`}
      style={{ left: coords.left, top: coords.top, width }}
    >
      <span className={styles.arrow} />
      <span className={styles.body}>{content}</span>
    </div>
  );

  return (
    <>
      <span
        ref={containerRef}
        className={`${styles.wrapper} ${className || ''}`}
        {...eventHandlers}
      >
        {children}
      </span>
      {tooltipEl && createPortal(tooltipEl, document.body)}
    </>
  );
}
